import { useQuery } from '@tanstack/react-query';
import { Info } from 'lucide-react';
import { BUILD, buildLabel } from '@/lib/build';
import { get } from '@/lib/api';
import { formatDate, useLocale, useT } from '@/i18n';

interface ApiBuild {
  version: string;
  build: string;
  sha: string;
  builtAt: string | null;
}

/** One-line build identity for the menu footer: v1.1.0 · #12 · 5094ef6 · 19 окт. 2026 г. */
export function BuildLine({ className }: { className?: string }) {
  const { locale } = useLocale();
  return (
    <div className={className} title={BUILD.time} data-testid="build-line">
      {buildLabel(locale)}
    </div>
  );
}

/** Settings → About: web and server builds side by side (they differ only mid-deploy). */
export function AboutSection() {
  const t = useT();
  const { locale } = useLocale();
  const api = useQuery({ queryKey: ['api-build'], queryFn: () => get<ApiBuild>('/health/live'), staleTime: 60_000 });
  const row = (label: string, v: { version: string; number: string; sha: string; time: string | null }) => (
    <div className="grid grid-cols-[110px_1fr] gap-x-3 gap-y-0.5 text-[13.5px]">
      <span className="text-muted">{label}</span>
      <span className="font-medium">
        v{v.version} · {t('settings.about.build')} #{v.number}
      </span>
      <span />
      <span className="font-mono text-[12px] text-muted">
        {v.sha} · {formatDate(locale, v.time)}
      </span>
    </div>
  );
  return (
    <section className="rounded-lg border border-border bg-surface p-5" aria-labelledby="about-title">
      <h2 id="about-title" className="mb-3 flex items-center gap-2 text-[15px] font-semibold">
        <Info className="size-4 text-muted" /> {t('settings.about.title')}
      </h2>
      <div className="space-y-3">
        {row(t('settings.about.app'), BUILD)}
        {api.data && row(t('settings.about.server'), { version: api.data.version, number: api.data.build, sha: api.data.sha, time: api.data.builtAt })}
      </div>
    </section>
  );
}
